import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { t } from 'i18next';
import { PASSWORD_MIN_LENGTH } from '../constants/password';
import type { I18nPrefix } from '../i18n/prefix';
import { estimatePasswordStrength, type PasswordLevel } from '../lib/password-strength';

const COMPONENT = 'components:password_strength_meter' satisfies I18nPrefix;

const SEGMENTS = 4;

type Tone = { key: 'too_short' | 'weak' | 'good' | 'strong'; bar: string; text: string };

const LEVELS: Record<Exclude<PasswordLevel, 0>, Tone> = {
  1: { key: 'too_short', bar: 'bg-spark-danger-solid', text: 'text-spark-text-danger' },
  2: { key: 'weak', bar: 'bg-spark-danger-solid', text: 'text-spark-text-danger' },
  3: { key: 'good', bar: 'bg-spark-warning-solid', text: 'text-spark-warning-soft-text' },
  4: { key: 'strong', bar: 'bg-spark-success-solid', text: 'text-spark-success-soft-text' },
};

type PasswordStrengthMeterProps = {
  password: string;
};

/** Always rendered, so the form doesn't shift on the first key. */
export function PasswordStrengthMeter({ password }: PasswordStrengthMeterProps) {
  const { level, missing } = estimatePasswordStrength(password);
  const tone = level === 0 ? null : LEVELS[level];

  return (
    <div className="flex flex-col gap-1.5 text-xs">
      {/* The label carries the level for assistive tech. */}
      <div aria-hidden className="flex gap-1.5">
        {Array.from({ length: SEGMENTS }, (_, segment) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed length list that never reorders
            key={segment}
            className={cn(
              'h-1.5 flex-1 rounded-full bg-spark-border-default transition-colors motion-reduce:transition-none',
              segment < level && tone?.bar,
            )}
          />
        ))}
      </div>
      <p aria-live="polite" className="min-h-lh text-spark-text-secondary">
        {tone ? (
          <span className={cn('font-semibold', tone.text)}>
            {t(`${COMPONENT}.levels.${tone.key}`)}
          </span>
        ) : (
          t(`${COMPONENT}.empty`, { min: PASSWORD_MIN_LENGTH })
        )}
        {level === 1 && ` ${t(`${COMPONENT}.missing`, { count: missing })}`}
      </p>
    </div>
  );
}
